/**
 * Production-safe logger that filters debug messages based on user role
 *
 * Behavior:
 * - Development: Always logs everything
 * - Production (admin user): Logs based on verbose toggle (default: off)
 * - Production (regular user): Only errors and critical warnings
 *
 * Toggle verbose logging in browser console:
 *   vitly.enableLogs()   - Enable verbose logging
 *   vitly.disableLogs()  - Disable verbose logging
 *   vitly.logStatus()    - Check current logging status
 *
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   logger.debug('Debug info', data);
 *   logger.info('Info message');
 *   logger.warn('Warning message');
 *   logger.error('Error message', error);
 */

// Admin email list from environment
const ADMIN_EMAILS = import.meta.env.VITE_ADMIN_EMAILS?.split(',').map((e: string) => e.trim()) || [];
const IS_DEV = import.meta.env.DEV;
const VERBOSE_STORAGE_KEY = 'vitly_verbose_logs';

// Check if current user is an admin
let isAdmin = false;

// Verbose logging toggle (stored in localStorage)
let verboseLogging = typeof window !== 'undefined' 
  ? localStorage.getItem(VERBOSE_STORAGE_KEY) === 'true'
  : false;

/**
 * Enable verbose logging for admin users
 */
export function enableVerboseLogs(): void {
  verboseLogging = true;
  if (typeof window !== 'undefined') {
    localStorage.setItem(VERBOSE_STORAGE_KEY, 'true');
  }
  console.log('[Logger] Verbose logging ENABLED');
}

/**
 * Disable verbose logging
 */
export function disableVerboseLogs(): void {
  verboseLogging = false;
  if (typeof window !== 'undefined') {
    localStorage.setItem(VERBOSE_STORAGE_KEY, 'false');
  }
  console.log('[Logger] Verbose logging DISABLED');
}

/**
 * Check current logging status
 */
export function getLogStatus(): { isAdmin: boolean; verboseLogging: boolean; isDev: boolean } {
  const status = { isAdmin, verboseLogging, isDev: IS_DEV };
  console.log('[Logger] Status:', status);
  return status;
}

// Expose toggle functions on window for console access
if (typeof window !== 'undefined') {
  (window as any).vitly = {
    ...(window as any).vitly,
    enableLogs: enableVerboseLogs,
    disableLogs: disableVerboseLogs,
    logStatus: getLogStatus
  };
}

/**
 * Initialize logger with current user email
 * Call this after authentication to enable admin logging
 */
export function initLogger(userEmail: string | null) {
  isAdmin = userEmail ? ADMIN_EMAILS.includes(userEmail) : false;

  if (IS_DEV) {
    console.log('[Logger] Initialized', { userEmail, isAdmin, isDev: IS_DEV, verboseLogging });
  }
}

/**
 * Check if logging is enabled for the current user
 */
function shouldLog(level: 'debug' | 'info' | 'warn' | 'error'): boolean {
  // Always log in development
  if (IS_DEV) return true;

  // Always show errors
  if (level === 'error') return true;

  // In production, admins need verbose toggle enabled
  if (isAdmin) return verboseLogging;

  return false;
}

/**
 * Logger service with role-based filtering
 */
export const logger = {
  /**
   * Debug messages - only visible to admins in production
   */
  debug: (...args: any[]) => {
    if (shouldLog('debug')) {
      console.log('[DEBUG]', ...args);
    }
  },

  /**
   * Info messages - only visible to admins in production
   */
  info: (...args: any[]) => {
    if (shouldLog('info')) {
      console.info('[INFO]', ...args);
    }
  },

  /**
   * Warning messages - only visible to admins in production
   */
  warn: (...args: any[]) => {
    if (shouldLog('warn')) {
      console.warn('[WARN]', ...args);
    }
  },

  /**
   * Error messages - always visible (critical for debugging)
   */
  error: (...args: any[]) => {
    console.error('[ERROR]', ...args);
  },

  /**
   * Success messages - only visible to admins in production
   */
  success: (...args: any[]) => {
    if (shouldLog('info')) {
      console.log('[SUCCESS]', ...args);
    }
  },

  /**
   * Group logs together - only in dev or for admins
   */
  group: (label: string) => {
    if (shouldLog('debug')) {
      console.group(label);
    }
  },

  groupEnd: () => {
    if (shouldLog('debug')) {
      console.groupEnd();
    }
  },

  /**
   * Table output - only in dev or for admins
   */
  table: (data: any) => {
    if (shouldLog('debug')) {
      console.table(data);
    }
  },

  /**
   * Time tracking - only in dev or for admins
   */
  time: (label: string) => {
    if (shouldLog('debug')) {
      console.time(label);
    }
  },

  timeEnd: (label: string) => {
    if (shouldLog('debug')) {
      console.timeEnd(label);
    }
  },
};

// Export utility functions
export { shouldLog };

// Export default for convenience
export default logger;
